import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { FileText, Banknote, Smartphone, Building2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { dashboardApi } from '@/lib/api';
import type { BillingDashboardData } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, GenericBadge,
  DashboardShell, ScrollTable, Th, Td, LoadingState, ErrorState,
} from './_shared';

function inr(n: number) { return '₹' + Math.round(n).toLocaleString('en-IN'); }
function shortInr(n: number) {
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)    return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n}`;
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-navy mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="text-gray-600">{shortInr(p.value)}</p>
      ))}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
export default function BillingDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<BillingDashboardData>({
    queryKey:        ['dashboard-billing'],
    queryFn:         () => dashboardApi.billing().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  return (
    <DashboardShell
      title="Billing Dashboard"
      subtitle="Invoice queue · collections · outstanding receivables"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard icon={FileText}   label="Draft Invoices"      value={data.draftInvoices}  accent="bg-amber-50" alert={data.draftInvoices > 0}/>
        <MetricCard icon={Banknote}   label="Cash Collected"      value={shortInr(data.totalCash)}  accent="bg-emerald-50"/>
        <MetricCard icon={Smartphone} label="UPI / Digital"       value={shortInr(data.totalUpi)}   accent="bg-blue-50"/>
        <MetricCard icon={Building2}  label="Pending Corporate"   value={shortInr(data.pendingCorporate)} accent="bg-red-50" alert/>
      </MetricsGrid>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Revenue by Category chart */}
        <SectionCard title="Today's Revenue by Category">
          <div className="px-5 py-4">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.reconItems} barSize={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis
                  dataKey="category"
                  tick={{ fontSize: 9, fill: '#9ca3af' }}
                  axisLine={false} tickLine={false}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={40}
                />
                <YAxis
                  tickFormatter={shortInr}
                  tick={{ fontSize: 9, fill: '#9ca3af' }}
                  axisLine={false} tickLine={false} width={48}
                />
                <Tooltip content={<ChartTooltip/>}/>
                <Bar dataKey="amount" name="Amount" fill="#E8A020" radius={[4, 4, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        {/* Recon Summary table */}
        <SectionCard title="Collection Summary">
          <ScrollTable>
            <thead>
              <tr>
                <Th>Category</Th><Th right>Transactions</Th><Th right>Amount</Th>
              </tr>
            </thead>
            <tbody>
              {data.reconItems.map(r => (
                <tr key={r.category} className="hover:bg-gray-50 transition">
                  <Td><span className="font-medium">{r.category}</span></Td>
                  <Td right muted>{r.count}</Td>
                  <Td right><span className="font-semibold text-navy">{inr(r.amount)}</span></Td>
                </tr>
              ))}
              <tr className="bg-gray-50">
                <Td><span className="font-bold text-navy">Total</span></Td>
                <Td right muted>{data.reconItems.reduce((s, r) => s + r.count, 0)}</Td>
                <Td right>
                  <span className="font-bold text-navy">{inr(data.reconItems.reduce((s, r) => s + r.amount, 0))}</span>
                </Td>
              </tr>
            </tbody>
          </ScrollTable>
        </SectionCard>
      </div>

      {/* Outstanding Receivables */}
      <SectionCard title={`Outstanding Receivables (${data.receivables.length})`}>
        <ScrollTable>
          <thead>
            <tr>
              <Th>Invoice</Th><Th>Patient</Th><Th>Type</Th>
              <Th right>Amount</Th><Th>Due Date</Th><Th>Days Pending</Th>
            </tr>
          </thead>
          <tbody>
            {data.receivables.map(r => (
              <tr key={r.invoiceNumber} className="hover:bg-gray-50 transition">
                <Td><span className="font-mono text-xs text-navy">{r.invoiceNumber}</span></Td>
                <Td><span className="font-medium">{r.patientName}</span></Td>
                <Td>
                  <GenericBadge
                    label={r.type}
                    variant={r.type === 'IPD' ? 'info' : 'neutral'}
                  />
                </Td>
                <Td right><span className="font-semibold text-navy">{inr(r.amount)}</span></Td>
                <Td muted>{r.dueDate}</Td>
                <Td>
                  <span className={clsx(
                    'text-xs font-semibold',
                    r.daysPending > 15 ? 'text-red-600' : r.daysPending > 7 ? 'text-amber-600' : 'text-gray-500',
                  )}>
                    {r.daysPending}d
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </SectionCard>

    </DashboardShell>
  );
}
